/**
 * S2.5 — pure editing model for the ad-hoc bracket grid in
 * EditQuantityPricingModal (the sealed §2.2 build gap).
 *
 * Component-free on purpose: the house vitest env is plain node (no jsdom),
 * so the grid's behavior is pinned here — validation, sort-on-save, the
 * DETACH copy, and the XOR-by-construction commit builder — while the tsx
 * only renders. Percent-only primitive (binding fence): a bracket is
 * minQuantity + discountPct, nothing else.
 */

import type {
  ProductPricingFields,
  QuantityBracket,
  QuantityPricingScope,
} from '@/types/api/pricing';

export type SourceMode = 'schedule' | 'ad_hoc' | 'none';

/** Per-row validation flags — indexes into the UNSORTED grid rows. */
export interface AdHocBracketIssues {
  /**
   * A later row repeats an earlier row's minQuantity (thresholds must be
   * duplicate-free — only the LATER occurrence is flagged, ScheduleEditorModal
   * superRefine precedent).
   */
  duplicateIndexes: number[];
  /** discountPct outside 0..100, or minQuantity < 1 / non-integer. */
  outOfRangeIndexes: number[];
}

export function validateAdHocBrackets(
  brackets: readonly QuantityBracket[]
): AdHocBracketIssues {
  const seen = new Set<number>();
  const duplicateIndexes: number[] = [];
  const outOfRangeIndexes: number[] = [];
  brackets.forEach((bracket, index) => {
    if (seen.has(bracket.minQuantity)) duplicateIndexes.push(index);
    seen.add(bracket.minQuantity);
    if (
      !Number.isInteger(bracket.minQuantity) ||
      bracket.minQuantity < 1 ||
      bracket.discountPct < 0 ||
      bracket.discountPct > 100
    ) {
      outOfRangeIndexes.push(index);
    }
  });
  return { duplicateIndexes, outOfRangeIndexes };
}

export const hasAdHocBracketIssues = (issues: AdHocBracketIssues): boolean =>
  issues.duplicateIndexes.length > 0 || issues.outOfRangeIndexes.length > 0;

/**
 * Ascending copy — the grid keeps entry order while typing; the SAVE is the
 * sorted ladder (host precedent: GroupMatrixTab sorts ScheduleEditorModal
 * output the same way). With duplicates blocked, sorted == strictly ascending.
 */
export const sortAdHocBrackets = (
  brackets: readonly QuantityBracket[]
): QuantityBracket[] => [...brackets].sort((a, b) => a.minQuantity - b.minQuantity);

/** DA-8 warn-only predicate: a ≥100% row zeroes the resolved price. */
export const hasFullDiscountBracket = (
  brackets: readonly QuantityBracket[] | null | undefined
): boolean => (brackets ?? []).some((bracket) => (bracket?.discountPct ?? 0) >= 100);

/**
 * DETACH copy (C2 A1) — row CLONES, so later grid edits can never write back
 * into the schedule the product just detached from.
 */
export const copyScheduleBrackets = (
  brackets: readonly QuantityBracket[]
): QuantityBracket[] => brackets.map((bracket) => ({ ...bracket }));

/**
 * XOR by construction (DD!116): exactly one side survives the commit no
 * matter what stale state the modal still holds — editing an ad-hoc set can
 * never resurrect the schedule reference, and picking a schedule always
 * drops the brackets. `applyProductPricingSave` re-checks and throws
 * 'SCHEDULE_EITHER_OR' as the store-side backstop.
 */
export function buildQuantityPricingSaveFields(
  base: ProductPricingFields | undefined,
  productId: string,
  mode: SourceMode,
  scheduleId: string | null,
  adHoc: readonly QuantityBracket[],
  scope: QuantityPricingScope
): ProductPricingFields {
  return {
    ...(base ?? { productId }),
    productId,
    quantityScheduleId: mode === 'schedule' ? scheduleId : null,
    quantityBrackets: mode === 'ad_hoc' ? sortAdHocBrackets(adHoc) : null,
    quantityScope: scope,
  };
}
